import { useRef, type ReactNode } from 'react';
import { useCloseWhenDetached } from 'crono-fe-common/hooks/useCloseWhenDetached';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@crono-one/design-system';
import { cn } from 'utils/style-utils';
import {
  toSideAndAlign,
  type OverlayPlacement,
  type OverlayProps,
} from './overlay.types';

interface ControlledPopoverProps {
  open: boolean;
  /** Called on Escape and on a click or focus outside both anchor and popover. */
  onClose: () => void;
  content: ReactNode;
  /** The anchor. It toggles `open` itself, e.g. from its own onClick. */
  children: ReactNode;
  placement?: OverlayPlacement;
  overlayProps?: OverlayProps;
  /**
   * Close, instead of only hiding, once the anchor is scrolled out of the
   * overlay's boundary (see `useTableOverlayProps`), so it doesn't come back
   * when the anchor scrolls back.
   */
  closeWhenDetached?: boolean;
  className?: string;
  anchorClassName?: string;
}

/**
 * A DS popover opened and closed by its parent. The anchor keeps focus when it
 * opens (a tag's search input lives in the anchor), and a click on the anchor
 * is left to the anchor's own handler instead of closing the popover first.
 * Clicks inside overlays portalled from the content count as inside.
 */
export function ControlledPopover({
  open,
  onClose,
  content,
  children,
  placement = 'bottom',
  overlayProps,
  closeWhenDetached = false,
  className,
  anchorClassName,
}: ControlledPopoverProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  useCloseWhenDetached({
    anchorRef,
    enabled: open && closeWhenDetached,
    overlayProps,
    onClose,
  });
  const { side, align } = toSideAndAlign(placement);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <PopoverAnchor asChild>
        <div ref={anchorRef} className={anchorClassName}>
          {children}
        </div>
      </PopoverAnchor>
      <PopoverContent
        side={side}
        align={align}
        className={cn('z-5001 w-auto max-w-112.5 p-0', className)}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
        {...overlayProps}
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}
